/**
 * WebPushSection — Settings > Notifications cards for web push.
 *
 * Two cards bundled here because they belong to the same feature but
 * have very different state:
 *   1. Subscription — tied to this specific browser (one row per device
 *      on the server). Renders a single on/off toggle.
 *   2. Rules — tied to the user account (apply to every subscribed
 *      browser). CRUD table with allow/block + geofence + alert-types +
 *      severity.
 *
 * Shown inside the existing NotificationsTab. Rendered conditionally in
 * the parent based on useWebPush().status so we don't confuse an operator
 * on an unsupported browser with a toggle that does nothing.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BellAlertIcon,
  PlusIcon,
  TrashIcon,
  ShieldCheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useWebPush, type PushStatus } from '../../hooks/useWebPush';
import {
  pushApi,
  type NotificationRule,
  type NotificationRuleInput,
} from '../../services/pushApi';
import { geofencesApi } from '../../services/api';

// Order matters here — rendered as a multi-select checkbox list in the
// rule editor. Mirrors the alert_type enum on the server.
const ALERT_TYPES = [
  'GEOFENCE_BREACH',
  'AIRSPACE_INTRUSION',
  'TRACK_LOST',
  'SENSOR_OFFLINE',
  'MISSION_DEVIATION',
  'SYSTEM_WARNING',
  'RISK_THRESHOLD',
] as const;

// Human-readable labels so the list doesn't read like an enum dump.
const ALERT_TYPE_LABEL: Record<string, string> = {
  GEOFENCE_BREACH: 'Geofence breach',
  AIRSPACE_INTRUSION: 'Airspace intrusion',
  TRACK_LOST: 'Track lost',
  SENSOR_OFFLINE: 'Sensor offline',
  MISSION_DEVIATION: 'Mission deviation',
  SYSTEM_WARNING: 'System warning',
  RISK_THRESHOLD: 'Risk threshold',
};

const STATUS_COPY: Record<PushStatus, { label: string; hint: string }> = {
  unsupported: {
    label: 'Not supported',
    hint: 'This browser cannot receive push notifications.',
  },
  'server-off': {
    label: 'Disabled on server',
    hint: 'The API has no VAPID keys configured — ask an admin to set VAPID_PUBLIC_KEY / VAPID_PRIVATE_KEY.',
  },
  unregistered: { label: 'Loading…', hint: '' },
  denied: {
    label: 'Permission denied',
    hint: 'Re-enable notifications in your browser settings for this site, then reload.',
  },
  off: {
    label: 'Off',
    hint: 'Turn on to receive SkyWatch alerts even when the tab is in the background.',
  },
  on: {
    label: 'On',
    hint: 'This browser will receive push notifications for matching alerts.',
  },
};

// ────────────────────────────────────────────────────────────────────────────
// Subscription card
// ────────────────────────────────────────────────────────────────────────────
function SubscriptionCard() {
  const { status, enable, disable, busy, error } = useWebPush();
  const copy = STATUS_COPY[status];
  const isOn = status === 'on';
  const canToggle = status === 'off' || status === 'on';

  return (
    <div className="glass-card p-6">
      <div className="flex items-start gap-3 mb-2">
        <BellAlertIcon className="h-6 w-6 text-accent mt-0.5" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-white">Browser Push</h3>
          <p className="text-sm text-gray-500">
            Desktop notifications for this browser, even when the tab isn't
            in focus.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={clsx(
              'text-xs font-medium px-2 py-0.5 rounded-full',
              isOn
                ? 'bg-accent/20 text-accent'
                : status === 'denied'
                  ? 'bg-red-500/20 text-red-400'
                  : 'bg-navy-700 text-gray-400',
            )}
          >
            {copy.label}
          </span>
          <button
            type="button"
            disabled={!canToggle || busy}
            onClick={() => (isOn ? disable() : enable())}
            className={clsx(
              'relative w-11 h-6 rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
              isOn ? 'bg-accent' : 'bg-navy-600',
            )}
          >
            <span
              className={clsx(
                'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
                isOn && 'translate-x-5',
              )}
            />
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-500 pl-9">{copy.hint}</p>
      {error && (
        <p className="text-xs text-red-400 mt-2 pl-9">Error: {error}</p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Rules card
// ────────────────────────────────────────────────────────────────────────────
function RulesCard() {
  const queryClient = useQueryClient();
  const [editor, setEditor] = useState<NotificationRuleInput | null>(null);

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['notificationRules'],
    queryFn: pushApi.listRules,
  });

  // Load geofences for the dropdown. Cheap query — fences are few and
  // change rarely, so a 5-min staleTime prevents refetch on every
  // rule-editor open.
  const { data: geofencesRaw } = useQuery({
    queryKey: ['geofences-for-rules'],
    queryFn: () => geofencesApi.list(),
    staleTime: 5 * 60 * 1000,
  });
  const geofences: Array<{ id: string; name: string }> = useMemo(() => {
    const list = (geofencesRaw as { data?: Array<{ id: string; name: string }> } | undefined)?.data;
    return Array.isArray(list) ? list : [];
  }, [geofencesRaw]);

  const createMutation = useMutation({
    mutationFn: (rule: NotificationRuleInput) => pushApi.createRule(rule),
    onSuccess: () => {
      toast.success('Notification rule added');
      setEditor(null);
      queryClient.invalidateQueries({ queryKey: ['notificationRules'] });
    },
    onError: () => toast.error('Failed to save rule'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pushApi.deleteRule(id),
    onSuccess: () => {
      toast.success('Rule removed');
      queryClient.invalidateQueries({ queryKey: ['notificationRules'] });
    },
    onError: () => toast.error('Failed to delete rule'),
  });

  const openNewRule = useCallback(
    () => setEditor({ mode: 'ALLOW', geofenceId: null, alertTypes: [], minSeverity: null, label: '' }),
    [],
  );

  return (
    <div className="glass-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white">Notification Rules</h3>
          <p className="text-sm text-gray-500">
            Allow-list or block-list specific alerts. With no rules, every
            alert is delivered. Add an ALLOW to narrow; add a BLOCK to
            silence noisy categories.
          </p>
        </div>
        <button
          type="button"
          onClick={openNewRule}
          className="flex items-center gap-1.5 text-xs text-navy-900 bg-accent hover:bg-accent/90 rounded px-3 py-1.5 font-semibold transition-colors"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add rule
        </button>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500 py-6 text-center">Loading rules…</div>
      ) : rules.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-navy-700 rounded-lg">
          No rules yet — every alert will be delivered to subscribed browsers.
        </div>
      ) : (
        <div className="divide-y divide-navy-800 border border-navy-800 rounded-lg overflow-hidden">
          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              rule={rule}
              geofences={geofences}
              onDelete={() => deleteMutation.mutate(rule.id)}
            />
          ))}
        </div>
      )}

      {editor && (
        <RuleEditor
          draft={editor}
          geofences={geofences}
          onChange={setEditor}
          onCancel={() => setEditor(null)}
          onSave={() => createMutation.mutate(editor)}
          saving={createMutation.isPending}
        />
      )}
    </div>
  );
}

function RuleRow({
  rule,
  geofences,
  onDelete,
}: {
  rule: NotificationRule;
  geofences: Array<{ id: string; name: string }>;
  onDelete: () => void;
}) {
  const fenceName = rule.geofenceId
    ? geofences.find((g) => g.id === rule.geofenceId)?.name ?? 'Unknown fence'
    : 'Any geofence';

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 bg-navy-900/40">
      <div className="flex items-center gap-3 min-w-0">
        {rule.mode === 'ALLOW' ? (
          <ShieldCheckIcon className="h-5 w-5 text-accent flex-shrink-0" />
        ) : (
          <NoSymbolIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
        )}
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className={clsx(
                'text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded',
                rule.mode === 'ALLOW'
                  ? 'bg-accent/20 text-accent'
                  : 'bg-red-500/20 text-red-400',
              )}
            >
              {rule.mode}
            </span>
            {rule.label && (
              <span className="text-sm font-medium text-white truncate">
                {rule.label}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {fenceName}
            {rule.alertTypes.length > 0
              ? ` · ${rule.alertTypes.map((t) => ALERT_TYPE_LABEL[t] ?? t).join(', ')}`
              : ' · all alert types'}
            {rule.minSeverity ? ` · severity ≥ ${rule.minSeverity}` : ''}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
        aria-label="Delete rule"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function RuleEditor({
  draft,
  geofences,
  onChange,
  onCancel,
  onSave,
  saving,
}: {
  draft: NotificationRuleInput;
  geofences: Array<{ id: string; name: string }>;
  onChange: (r: NotificationRuleInput) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const toggleType = (type: string) => {
    const next = draft.alertTypes ?? [];
    onChange({
      ...draft,
      alertTypes: next.includes(type) ? next.filter((t) => t !== type) : [...next, type],
    });
  };

  return (
    <div className="mt-4 p-4 bg-navy-900/60 border border-navy-700 rounded-lg space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex gap-1 bg-navy-800 rounded-lg p-1 border border-navy-700">
          {(['ALLOW', 'BLOCK'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => onChange({ ...draft, mode })}
              className={clsx(
                'px-3 py-1 rounded-md text-xs font-medium transition-all',
                draft.mode === mode
                  ? mode === 'ALLOW'
                    ? 'bg-accent text-navy-900'
                    : 'bg-red-500 text-white'
                  : 'text-gray-400 hover:text-white',
              )}
            >
              {mode}
            </button>
          ))}
        </div>
        <input
          type="text"
          value={draft.label ?? ''}
          onChange={(e) => onChange({ ...draft, label: e.target.value })}
          placeholder="Label (optional)"
          className="flex-1 px-3 py-1.5 bg-navy-800 border border-navy-700 rounded text-white text-sm placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-accent/30"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs text-gray-400 block mb-1">Geofence</span>
          <select
            value={draft.geofenceId ?? ''}
            onChange={(e) =>
              onChange({ ...draft, geofenceId: e.target.value || null })
            }
            className="w-full px-3 py-1.5 bg-navy-800 border border-navy-700 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30"
          >
            <option value="">Any geofence</option>
            {geofences.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs text-gray-400 block mb-1">Minimum severity</span>
          <select
            value={draft.minSeverity ?? ''}
            onChange={(e) =>
              onChange({
                ...draft,
                minSeverity: (e.target.value || null) as NotificationRuleInput['minSeverity'],
              })
            }
            className="w-full px-3 py-1.5 bg-navy-800 border border-navy-700 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-accent/30"
          >
            <option value="">Any</option>
            <option value="INFO">Info and up</option>
            <option value="WARNING">Warning and up</option>
            <option value="CRITICAL">Critical only</option>
          </select>
        </label>
      </div>

      <div>
        <span className="text-xs text-gray-400 block mb-1">
          Alert types <span className="text-gray-600">(empty = all)</span>
        </span>
        <div className="flex flex-wrap gap-2">
          {ALERT_TYPES.map((type) => {
            const active = (draft.alertTypes ?? []).includes(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                className={clsx(
                  'text-xs px-2.5 py-1 rounded border transition-colors',
                  active
                    ? 'bg-accent/20 border-accent text-accent'
                    : 'bg-navy-800 border-navy-700 text-gray-400 hover:text-white',
                )}
              >
                {ALERT_TYPE_LABEL[type]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="text-xs text-gray-400 hover:text-white px-3 py-1.5 rounded transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="text-xs text-navy-900 bg-accent hover:bg-accent/90 rounded px-3 py-1.5 font-semibold transition-colors disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save rule'}
        </button>
      </div>
    </div>
  );
}

export default function WebPushSection() {
  return (
    <>
      <SubscriptionCard />
      <RulesCard />
    </>
  );
}
